import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function useProfileScore(userId: string | null) {
  return useQuery({
    queryKey: ['profile-score', userId],
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase.functions.invoke('calculate-profile-score', {
        body: { userId }
      });

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
}

export function useProfileCompleteness(userId: string | null) {
  return useQuery({
    queryKey: ['profile-completeness', userId],
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from('profile_completeness')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
}

export function useRecalculateScore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      console.log('[Profile Score] Starting recalculation for user:', userId);
      
      const { data, error } = await supabase.functions.invoke('calculate-profile-score', {
        body: { userId }
      });

      if (error) {
        console.error('[Profile Score] Recalculation failed:', error);
        throw error;
      }
      
      console.log('[Profile Score] Recalculation successful:', data);
      return data;
    },
    onSuccess: (data, variables) => {
      console.log('[Profile Score] Invalidating queries for user:', variables.userId);
      queryClient.invalidateQueries({ queryKey: ['profile-score', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['profile-completeness', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['profile', variables.userId] });
    },
    onError: (error: Error) => {
      console.error('[Profile Score] Mutation error:', error);
    },
  });
}